import * as Sentry from '@sentry/browser';
import React from 'react';
import { Location } from 'react-router';
import { logout } from '../../store/authentication/actions';
import { IConnectProps } from '../../utils/IConnectProps';
import { Component } from './Component';
import { Forbidden } from './Forbidden';
import { GeneralError } from './GeneralError';

interface ICatchingComponentState {
    hasError: boolean;
    isForbidden: boolean;
    isUnauthorized: boolean;
    sentryEventId?: string | undefined;
}

interface ICatchingComponentProps {
    children: React.ReactNode;
    location: Location;
}

export class CatchingComponent extends Component<ICatchingComponentProps, ICatchingComponentState> {
    constructor(props: ICatchingComponentProps) {
        super(props);
        this.state = {
            hasError: false,
            isForbidden: false,
            isUnauthorized: false,
        };
    }

    public override componentDidMount() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window.addEventListener('unhandledrejection', this.handleUnhandledRejection.bind(this));
    }

    public override componentWillUnmount() {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        window.removeEventListener('unhandledrejection', this.handleUnhandledRejection as EventListenerOrEventListenerObject);
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    public override UNSAFE_componentWillReceiveProps(nextProps: ICatchingComponentProps) {
        if (this.props.location !== nextProps.location) {
            void this.setState({ hasError: false, isForbidden: false });
        }
    }

    public override componentDidCatch(error: any, info: React.ErrorInfo) {
        console.log('componentDidCatch error', error); // TODO: remove after testing with Sentry ZXY-1101
        if (error.statusCode === 401) {
            void this.setState({ isUnauthorized: true });
        } else if (!PRODUCTION) {
            throw error;
        } else {
            this.logErrorToSentry(error, info);
        }
    }

    public override render() {
        if (this.state.hasError) {
            return <GeneralError sentryEventId={this.state.sentryEventId} />;
        } else if (this.state.isUnauthorized) {
            // Nothing needs to render, because a logout action is also dispatched
            return null;
        } else if (this.state.isForbidden) {
            return <Forbidden />;
        } else if (this.props.children) {
            return <>{this.props.children}</>;
        }
        return null;
    }

    private handleUnhandledRejection(promiseRejectionEvent: PromiseRejectionEvent) {
        promiseRejectionEvent.preventDefault();
        promiseRejectionEvent.stopImmediatePropagation();
        const reason = promiseRejectionEvent.reason || {};
        const { statusCode, message } = reason;
        console.log('handleUnhandledRejection reason', reason); // TODO: remove after testing with Sentry ZXY-1101

        if (statusCode === 401) {
            void this.setState({ isUnauthorized: true });
            (this.props as unknown as IConnectProps).dispatch(logout());
        } else if (statusCode === 403) {
            void this.setState({ isForbidden: true });
        } else if (statusCode === 502) {
            console.log('502 Bad gateway error');
        } else if (message === 'ResizeObserver loop limit exceeded') {
            console.error('ResizeObserver loop limit exceeded', promiseRejectionEvent);
        } else {
            promiseRejectionEvent.promise.catch((error) => {
                if (PRODUCTION) {
                    this.logErrorToSentry(error);
                } else {
                    console.error(error);
                }
            });
        }
    }

    private logErrorToSentry(error: unknown, info?: React.ErrorInfo) {
        Sentry.withScope((scope) => {
            if (info) {
                scope.setExtra('React error info', info);
            }

            Sentry.captureException(error);
        });

        void this.setState({ hasError: true, sentryEventId: Sentry.lastEventId() });
    }
}
